import { Button, Flex, Title } from "@mantine/core";
import { aulaService } from "../../api";
import { useMediaQuery } from "@mantine/hooks";
import { showNotification } from "@mantine/notifications";
import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { GenderLabLoader } from "../GenderLabLoader";
import { lessonContentError } from "../../constants/LessonContentError";
import { TLesson } from "../../types/Aula/TCourse";
import { useAuth } from "../../context";
import { FaExclamationTriangle } from "react-icons/fa";
import { renderNode } from "../../utils/renderNode";

interface LessonContentProps {
  lessonId: string;
  enrollmentId: string;
  nextLesson?: TLesson | null;
  isCompleted: boolean;
}

export const LessonContent = ({
  lessonId,
  enrollmentId,
  nextLesson,
  isCompleted,
}: LessonContentProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lessonContent, setLessonContent] = useState<any>(null);
  const [practiceQuizCompleted, setPracticeQuizCompleted] = useState(false);
  const isMobile = useMediaQuery("(max-width: 992px)");
  const practiceQuizRef = useRef<HTMLDivElement | null>(null);

  const buttonText = useMemo(
    () =>
      nextLesson
        ? "Completar y continuar"
        : "Finalizar y descargar certificado",
    [nextLesson]
  );

  const markCompleted = useCallback(async () => {
    if (!isCompleted && practiceQuizRef.current && !practiceQuizCompleted) {
      showNotification({
        title: "Cuestionario incompleto",
        message:
          "Por favor, completa el cuestionario de práctica antes de continuar.",
        color: "yellow",
        icon: <FaExclamationTriangle />,
        withBorder: true,
        autoClose: false,
      });
      return;
    }

    try {
      if (nextLesson) {
        await aulaService.markLessonAsCompleted(lessonId, enrollmentId);
        navigate(`/aula-virtual/${enrollmentId}/content/${nextLesson.id}`, {
          state: { category: nextLesson.category, lessonCompleted: true },
        });
      } else {
        const res = await aulaService.markCongratulationsAsCompleted(
          lessonId,
          enrollmentId
        );
        if (!res.data.certificate) {
          navigate(`/aula-virtual/${enrollmentId}`);
        } else {
          window.open(
            `/aula-virtual/${enrollmentId}/certificado-alumno`,
            "_blank"
          );
          sessionStorage.setItem("triggerDownload", "true");
        }
      }
    } catch (error) {
      console.error("Error marking lesson as completed:", error);
    }
  }, [
    isCompleted,
    practiceQuizCompleted,
    nextLesson,
    lessonId,
    enrollmentId,
    navigate,
    user,
  ]);

  const getLessonContent = useCallback(async () => {
    try {
      const res = await aulaService.getContent(lessonId);
      setLessonContent(res.data);
    } catch (error) {
      setLessonContent(lessonContentError);
      console.error("Error fetching lesson content:", error);
    }
  }, [lessonId]);

  useEffect(() => {
    setLessonContent(null);
    getLessonContent();
  }, [getLessonContent]);

  return (
    <>
      {lessonContent ? (
        <Flex
          direction="column"
          gap="md"
          // p=""
          w={isMobile ? "100%" : "75%"}
          m="auto"
        >
          <Title order={1}>{lessonContent.name}</Title>
          {renderNode(
            lessonContent.content,
            practiceQuizRef,
            setPracticeQuizCompleted
          )}
          {!lessonContent.hasErrors && (
            <Flex justify="center" mt="md">
              <Button onClick={markCompleted} size={isMobile ? "sm" : "md"}>
                {buttonText}
              </Button>
            </Flex>
          )}
        </Flex>
      ) : (
        <GenderLabLoader />
      )}
    </>
  );
};
